import ApiService, {ApiDelete, ApiPatch, ApiPost} from '../../../../shared/services/api-service';
import commonActions from '../../../../shared/store/common/action-creators';
import AxiosError from '../../../../shared/store/axios-error';
import notificationsActionsCreator from '../../notifications/action-creators';
import tagsActions from '../../tags/action-creators';
import usersCommitTypes from '../commit-types';

const userActionsCreators = {
  addUser() {
    return {
      isAddingUser: true,
      type: usersCommitTypes.SET_IS_ADDING_USER,
    };
  },
  attachUserTag(userGuid, tagGuid) {
    return async (dispatch) => {
      dispatch(this.setIsLoading('attachUserTag'));
      const data = await ApiService.post(`/api/v1/users/${userGuid}/tags`, {
        tagGuid,
      })
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('attachUserTag'));
      if (data.error) {
        commonActions.handleApiError(data.error);
      } else {
        dispatch(this.attachUserTagSuccess(data));
      }
    };
  },
  attachUserTagSuccess(updated) {
    return (dispatch) => {
      dispatch(this.replaceUser(updated.data));
      dispatch(notificationsActionsCreator.addSavedSuccessfully('User updated'));
    };
  },
  closeAddUser() {
    return {
      isAddingUser: false,
      type: usersCommitTypes.SET_IS_ADDING_USER,
    };
  },
  closeEditUser() {
    return {
      isEditingUser: null,
      type: usersCommitTypes.SET_IS_EDITING_USER,
    };
  },
  closeEditUserRoles() {
    return {
      isEditingUserRoles: false,
      type: usersCommitTypes.SET_IS_EDITING_USER_ROLES,
    };
  },
  createUser(formData) {
    return async (dispatch) => {
      dispatch(this.setIsLoading('createUser'));
      const data = await ApiService.post('/api/v1/users', {
        ...formData,
        email: formData.email.trim().toLowerCase()
      })
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('createUser'));
      if (data.error) {
        const action =
          data.error.status === 422
            ? this.createUserValidationErrors(data.error)
            : commonActions.handleApiError(data.error);
        dispatch(action);
      } else {
        dispatch(this.createUserSuccess(data));
      }
    };
  },
  changeOitDivision(formData) {
    return async (dispatch) => {
      dispatch(this.setIsLoading('changeDivision'));
      const data = await ApiService.patch('/api/v1/change-oit-division', {
        ...formData
      })
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('changeDivision'));
      if (data.error) {
        commonActions.handleApiError(data.error);
      } else {
        dispatch(this.changeDivisionSuccess());
      }
    };
  },
  createUserValidationErrors(errors) {
    return {
      validationErrors: AxiosError.unwrap422Errors(errors),
      type: usersCommitTypes.PUSH_USER_VALIDATION_ERRORS,
    };
  },
  createUserSuccess(created) {
    return (dispatch) => {
      dispatch(this.pushUser(created.data));
      dispatch(this.closeAddUser());
      dispatch(notificationsActionsCreator.addSavedSuccessfully('User created'));
    };
  },
  changeDivisionSuccess() {
    return (dispatch) => {
      dispatch(this.fetchUsers());
      dispatch(notificationsActionsCreator.addSavedSuccessfully('Division Changed'));
    };
  },
  detachUserTag(userGuid, tagGuid) {
    return async (dispatch) => {
      dispatch(this.setIsLoading('detachUserTag'));
      const data = await ApiService.delete(`/api/v1/users/${userGuid}/tags/${tagGuid}`)
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('detachUserTag'));
      if (data.error) {
        commonActions.handleApiError(data.error);
      } else {
        dispatch(this.detachUserTagSuccess(data));
      }
    };
  },
  detachUserTagSuccess(updated) {
    return (dispatch) => {
      dispatch(this.replaceUser(updated.data));
      dispatch(notificationsActionsCreator.addSavedSuccessfully('User updated'));
    };
  },
  editUser(isEditingUser) {
    return {
      isEditingUser,
      type: usersCommitTypes.SET_IS_EDITING_USER,
    };
  },
  editUserRoles() {
    return {
      isEditingUserRoles: true,
      type: usersCommitTypes.SET_IS_EDITING_USER_ROLES,
    };
  },
  fetchUser(userGuid) {
    return async (dispatch) => {
      dispatch(this.setIsLoading('fetchUser'));
      const data = await ApiService.get(`/api/v1/users/${userGuid}`)
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('fetchUser'));
      if (data.error) {
        dispatch(commonActions.handleApiError(data.error));
      } else {
        dispatch(this.setSelectedUser(data));
      }
    };
  },
  fetchUsers() {
    return async (dispatch, getState) => {
      if (!getState().users.list) {
        dispatch(this.setIsLoading('fetchUsers'));
        const data = await ApiService.get('/api/v1/users')
          .then(response => response.data)
          .catch(error => ({error}));
        dispatch(this.setIsLoading('fetchUsers'));
        if (data.error) {
          dispatch(commonActions.handleApiError(data.error));
        } else {
          dispatch(this.fetchUsersSuccess(data));
        }
      }
    };
  },
  fetchUsersByMaster() {
    return async (dispatch, getState) => {
      if (!getState().users.usersByMaster.length) {
        dispatch(this.setIsLoading('fetchUsers'));
        const data = await ApiService.get('/api/v1/users/by-master')
          .then(response => response.data)
          .catch(error => ({error}));

        dispatch(this.setIsLoading('fetchUsers'));
        if (data.error) {
          dispatch(commonActions.handleApiError(data.error));
        } else {
          dispatch({
            list: data.data,
            type: usersCommitTypes.PUSH_USERS_BY_MASTER,
          });
        }
      }
    };
  },
  fetchUsersSuccess(usersResponse) {
    return (dispatch) => {
      dispatch(this.pushUsers(usersResponse.data));
    };
  },
  enableDisableUser(userGuid, active) {
    return (dispatch) => {
      dispatch(this.setIsLoading('fetchUsers'));
      return ApiPatch(`/api/v1/users/${userGuid}/switch-active-local`, {
        activate: active
      }).then(res => ApiService.get('/api/v1/users')
        .then(response => response.data))
        .then(data => {
          dispatch(this.fetchUsersSuccess(data));
          dispatch(this.setIsLoading('fetchUsers'));
        })
    }
  },
  enableDisableLoginAccess(userGuid, active) {
    return (dispatch) => {
      dispatch(this.setIsLoading('fetchUsers'));
      return ApiPatch(`/api/v1/users/${userGuid}/switch-active`, {
        activate: active
      }).then(res => ApiService.get('/api/v1/users')
        .then(response => response.data))
        .then(data => {
          dispatch(this.fetchUsersSuccess(data));
          dispatch(this.setIsLoading('fetchUsers'));
        })
    }
  },
  loadUserContainer() {
    return (dispatch) => {
      dispatch(this.fetchUsers());
      dispatch(tagsActions.fetchTags());
    };
  },
  pushUser(user) {
    return {
      user,
      type: usersCommitTypes.PUSH_USER,
    };
  },
  pushUsers(list) {
    return {
      list,
      type: usersCommitTypes.PUSH_USERS,
    };
  },
  replaceUser(user) {
    return {
      user,
      type: usersCommitTypes.REPLACE_USER,
    };
  },
  setSelectedUser(selectedGuid) {
    return {
      selectedGuid,
      type: usersCommitTypes.SET_SELECTED_USER_GUID,
    };
  },
  setIsLoading(isLoading) {
    return {
      isLoading,
      type: usersCommitTypes.SET_USER_IS_LOADING,
    };
  },
  unmountUserContainer() {
    return {
      type: usersCommitTypes.RESET_USER_STATE,
    };
  },
  updateUser(formData) {
    const patch = {
      data: {...formData},
      patched: 'details',
    };
    // Remove redundant
    delete patch.data.userGuid;
    return async (dispatch) => {
      dispatch(this.setIsLoading('updateUserDetails'));
      const data = await ApiService.patch(
        `/api/v1/users/${formData.userGuid}`,
        patch,
      )
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('updateUserDetails'));
      if (data.error) {
        dispatch(commonActions.handleApiError(data.error));
        return Promise.reject();
      }
      dispatch(this.updateUserSuccess(data));
      return Promise.resolve();
    };
  },
  updateUserPhoto(formData) {
    const patch = {
      data: {...formData},
      patched: 'photo',
    };
    return async (dispatch) => {
      dispatch(this.setIsLoading('updateUserPhoto'));
      const data = await ApiService.patch(
        `/api/v1/users/${formData.userGuid}`,
        patch,
      )
        .then(response => response.data)
        .catch(error => ({error}));
      dispatch(this.setIsLoading('updateUserPhoto'));
      if (data.error) {
        dispatch(commonActions.handleApiError(data.error));
        return Promise.reject();
      }
      dispatch(this.updateUserSuccess(data));
      return Promise.resolve();
    };
  },
  addCertification(formData, userId) {
    return (dispatch) => {
      return ApiPost(`/api/v1/users/${userId}/certification`, {certification: formData})
        .then((data) => {
          dispatch(this.updateUserSuccess({data}));
        })
        .catch((err) => {
          dispatch(commonActions.handleApiError(err));
          return Promise.reject();
        })
    }
  },
  editCertification(formData, userId) {
    return (dispatch) => {
      return ApiPatch(`/api/v1/users/${userId}/certification`, {certification: formData})
        .then((data) => {
          dispatch(this.updateUserSuccess(data));
        })
        .catch((err) => {
          dispatch(commonActions.handleApiError(err));
          return Promise.reject();
        })
    }
  },
  deleteCertification(formData, userId) {
    return (dispatch) => {
      return ApiDelete(`/api/v1/users/${userId}/certification`, {id: formData.id})
        .then((data) => {
          dispatch(this.updateUserSuccess({data}));
        })
        .catch((err) => {
          dispatch(commonActions.handleApiError(err));
          return Promise.reject();
        })
    }
  },
  updateUserSuccess(updated) {
    return (dispatch) => {
      dispatch(this.replaceUser(updated.data));
      dispatch(notificationsActionsCreator.addSavedSuccessfully('User updated'));
      dispatch(this.closeEditUser());
    };
  },
};

export default userActionsCreators;
